
/**
 * @typedef {Object} DeliveryLocation
 * @property {string} country
 * @property {string} department
 */

/**
 * DeliveryWidget - A custom element for managing and displaying 
 * estimated delivery dates based on user department selection.
 */
class DeliveryWidget extends HTMLElement {
  /** @type {DeliveryLocation | null} */
  static _sharedLocation = null;
  static _initialized = false;

  constructor() {
    super();
    this.storageKey = 'senmart_delivery_location';
    this.departments = [
      "Alta Verapaz", "Baja Verapaz", "Chimaltenango", "Chiquimula",
      "El Progreso", "Escuintla", "Guatemala", "Huehuetenango",
      "Izabal", "Jalapa", "Jutiapa", "Petén", "Quetzaltenango",
      "Quiché", "Retalhuleu", "Sacatepéquez", "San Marcos",
      "Santa Rosa", "Sololá", "Suchitepéquez", "Totonicapán", "Zacapa"
    ];
    this._clickBound = false;
  }

  connectedCallback() {
    // 1. Try to recover state from memory or storage
    this.syncState();
    
    // 2. Initial render
    this.render();

    // 3. Add click listener
    if (!this._clickBound) {
      this.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (!target.closest('form') && !target.closest('button[type="submit"]')) {
          this.openModal();
        }
      });
      this._clickBound = true;
    }

    // 4. Force a re-render after a delay to catch late-load scripts or clones
    [10, 100, 500, 1000].forEach(delay => {
      setTimeout(() => {
        this.syncState();
        this.render();
      }, delay);
    });
  }

  /**
   * Syncs the local instance state with the shared state and localStorage
   */
  syncState() {
    if (DeliveryWidget._sharedLocation) return;

    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        DeliveryWidget._sharedLocation = JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Delivery Widget: Error reading location', e);
    }
  }

  /**
   * @param {string} department
   */
  saveLocation(department) {
    /** @type {DeliveryLocation} */
    const newLocation = {
      country: "Guatemala",
      department: department
    };
    
    // Save to shared state immediately
    DeliveryWidget._sharedLocation = newLocation;
    
    // Persist
    localStorage.setItem(this.storageKey, JSON.stringify(newLocation));
    
    // Notify all instances
    window.dispatchEvent(new CustomEvent('delivery-location-updated'));
  }

  /**
   * @param {string} department
   * @returns {{ min: string, max: string }}
   */
  calculateDeliveryDate(department) {
    const now = new Date();
    let minDays, maxDays;

    if (department === 'Guatemala') {
      minDays = 1;
      maxDays = 2;
    } else {
      minDays = 3;
      maxDays = 5;
    }

    /**
     * @param {Date} date
     * @param {number} days
     * @returns {Date}
     */
    const addBusinessDays = (date, days) => {
      const result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) {
          added++;
        }
      }
      return result;
    };

    const minDate = addBusinessDays(now, minDays);
    const maxDate = addBusinessDays(now, maxDays);

    /** @type {Intl.DateTimeFormatOptions} */
    const options = { day: 'numeric', month: 'long' };
    return {
      min: minDate.toLocaleDateString('es-GT', options),
      max: maxDate.toLocaleDateString('es-GT', options)
    };
  }

  render() {
    const container = this.querySelector('[data-delivery-content]');
    if (!container) return;

    const location = DeliveryWidget._sharedLocation;
    const mode = this.getAttribute('mode') || 'header';
    const isHeader = mode === 'header';

    if (location && location.department) {
      const dates = this.calculateDeliveryDate(location.department);
      if (isHeader) {
        container.innerHTML = `
          <span class="delivery-widget__text">Enviar a: <strong>${location.department}</strong></span>
          <span class="delivery-widget__arrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </span>
        `;
      } else {
        container.innerHTML = `
          <div class="delivery-widget__content">
            <span class="delivery-widget__title">Recíbelo entre el ${dates.min} y ${dates.max}</span>
            <span class="delivery-widget__info">Enviando a <strong>${location.department}</strong>, Guatemala</span>
          </div>
          <span class="delivery-widget__cta">Cambiar ubicación</span>
        `;
      }
    } else {
      // Fallback only if absolutely no location is found
      if (isHeader) {
        container.innerHTML = `
          <span class="delivery-widget__text">Seleccionar ubicación</span>
          <span class="delivery-widget__arrow">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </span>
        `;
      } else {
        container.innerHTML = `
          <div class="delivery-widget__content">
            <span class="delivery-widget__title">Calcula tu fecha de entrega</span>
            <span class="delivery-widget__info">Selecciona tu ubicación para ver tiempos estimados</span>
          </div>
          <span class="delivery-widget__cta">Elegir departamento</span>
        `;
      }
    }
  }

  openModal() {
    let modal = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog#delivery-modal'));
    if (!modal) {
      modal = this.createModal();
      document.body.appendChild(modal);
    }
    if (modal && typeof modal.showModal === 'function') {
      modal.showModal();
    }
  }

  /**
   * @returns {HTMLDialogElement}
   */
  createModal() {
    const dialog = /** @type {HTMLDialogElement} */ (document.createElement('dialog'));
    dialog.id = 'delivery-modal';
    dialog.className = 'delivery-modal';
    
    const location = DeliveryWidget._sharedLocation;
    const currentDept = location ? location.department : '';

    dialog.innerHTML = `
      <div class="delivery-modal__header">
        <h3 class="delivery-modal__title">Ubicación de entrega</h3>
        <button class="delivery-modal__close" data-close-modal aria-label="Cerrar">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <form class="delivery-modal__form">
        <div class="delivery-modal__field">
          <label class="delivery-modal__label">País</label>
          <select class="delivery-modal__select" disabled>
            <option value="Guatemala" selected>Guatemala</option>
          </select>
        </div>
        <div class="delivery-modal__field">
          <label class="delivery-modal__label">Departamento</label>
          <select class="delivery-modal__select" name="department" required>
            <option value="" disabled ${!currentDept ? 'selected' : ''}>Selecciona un departamento</option>
            ${this.departments.map(dept => `
              <option value="${dept}" ${dept === currentDept ? 'selected' : ''}>${dept}</option>
            `).join('')}
          </select>
        </div>
        <div class="delivery-modal__footer">
          <button type="submit" class="button button--primary delivery-modal__button">Guardar ubicación</button>
        </div>
      </form>
    `;

    const form = dialog.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const dept = formData.get('department');
        if (typeof dept === 'string') {
          this.saveLocation(dept);
        }
        dialog.close();
      });
    }

    const closeBtn = dialog.querySelector('[data-close-modal]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        dialog.close();
      });
    }
    
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) dialog.close();
    });

    return dialog;
  }
}

if (!customElements.get('delivery-widget')) {
  customElements.define('delivery-widget', DeliveryWidget);
}

// Global listeners to sync all instances instantly
window.addEventListener('delivery-location-updated', () => {
  document.querySelectorAll('delivery-widget').forEach(widget => {
    const deliveryWidget = /** @type {DeliveryWidget} */ (widget);
    if (deliveryWidget.render) deliveryWidget.render();
  });
});

window.addEventListener('storage', (e) => {
  if (e.key === 'senmart_delivery_location') {
    try {
      DeliveryWidget._sharedLocation = e.newValue ? JSON.parse(e.newValue) : null;
      document.querySelectorAll('delivery-widget').forEach(widget => {
        const deliveryWidget = /** @type {DeliveryWidget} */ (widget);
        if (deliveryWidget.render) deliveryWidget.render();
      });
    } catch (err) {}
  }
});

